import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { ArrowLeft, PanelRight, MessageSquare, BotOff } from 'lucide-react';
import { Conversation, Message } from '../../../types/models/whatsapp';
import MessageBubble from './MessageBubble';
import WindowCountdown from './WindowCountdown';

interface ChatCanvasProps {
  conversation: Conversation | null;
  messages: Message[];
  loading: boolean;
  /** False once the archive has no older pages for this thread. */
  hasMoreHistory: boolean;
  loadingHistory: boolean;
  onLoadHistory: () => void;
  onBack: () => void;
  onToggleContextPanel: () => void;
  onRetry: (messageId: string) => void;
  /** Rendered into the header — the chat options menu. */
  headerActions?: React.ReactNode;
  children?: React.ReactNode; // the composer
}

/** Distance from the top that triggers loading the previous page. */
const LOAD_MORE_THRESHOLD_PX = 120;

/**
 * Below this distance from the bottom, the view is considered "at the bottom"
 * and will auto-follow new messages. Above it, the agent has scrolled up to
 * read history and must not be yanked back down.
 */
const FOLLOW_THRESHOLD_PX = 150;

const dayKey = (timestamp: number): string =>
  new Date(timestamp).toDateString();

const formatDayLabel = (timestamp: number): string => {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString([], {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() === today.getFullYear() ? undefined : 'numeric'
  });
};

const ChatCanvas: React.FC<ChatCanvasProps> = ({
  conversation,
  messages,
  loading,
  hasMoreHistory,
  loadingHistory,
  onLoadHistory,
  onBack,
  onToggleContextPanel,
  onRetry,
  headerActions,
  children
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastMessageId = messages[messages.length - 1]?.id;
  const firstMessageId = messages[0]?.id;

  /** Whether the view is following new messages, rather than reading history. */
  const followingRef = useRef(true);
  /** Scroll height captured before an older page prepends. */
  const restoreRef = useRef<number | null>(null);

  // A new thread always starts pinned to the newest message.
  useEffect(() => {
    followingRef.current = true;
    restoreRef.current = null;
  }, [conversation?.id]);

  /**
   * Keep the viewport still when an older page prepends.
   *
   * Runs in a layout effect so the correction happens before paint — doing it
   * in a passive effect lets the browser show the jumped position for a frame.
   */
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || restoreRef.current === null) return;

    el.scrollTop = el.scrollHeight - restoreRef.current;
    restoreRef.current = null;
  }, [firstMessageId]);

  // Follow the newest message, but only when the agent hasn't scrolled away.
  useEffect(() => {
    const el = scrollRef.current;
    if (el && followingRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [conversation?.id, lastMessageId]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;

    followingRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < FOLLOW_THRESHOLD_PX;

    if (
      el.scrollTop < LOAD_MORE_THRESHOLD_PX &&
      hasMoreHistory &&
      !loadingHistory &&
      !loading
    ) {
      // Capture the pre-prepend height so the layout effect can restore it.
      restoreRef.current = el.scrollHeight;
      onLoadHistory();
    }
  };

  if (!conversation) {
    return (
      <div className="flex h-full flex-1 flex-col items-center justify-center bg-wa-chatBg">
        <MessageSquare className="h-12 w-12 text-gray-300" />
        <p className="mt-3 text-sm font-medium text-gray-500">
          Select a conversation
        </p>
        <p className="mt-1 text-xs text-gray-400">
          Choose a chat from the list to start replying.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-1 flex-col">
      <div className="flex items-center gap-3 border-b border-wa-border bg-wa-surface px-4 py-2.5">
        <button
          type="button"
          onClick={onBack}
          className="rounded p-1 text-gray-500 hover:bg-gray-100 md:hidden"
          aria-label="Back to conversations"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-wa-text">
            {conversation.contactName || conversation.contactPhone}
          </p>
          <p className="truncate text-xs text-wa-muted">
            +{conversation.contactPhone}
          </p>
        </div>

        {!conversation.botEnabled && (
          <span
            className="hidden shrink-0 items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-[11px] font-medium text-gray-600 sm:inline-flex"
            title="Automation is off for this chat"
          >
            <BotOff className="h-3 w-3" />
            Bot off
          </span>
        )}

        <WindowCountdown conversation={conversation} />

        <button
          type="button"
          onClick={onToggleContextPanel}
          className="rounded p-1.5 text-gray-500 hover:bg-gray-100"
          aria-label="Toggle contact details"
        >
          <PanelRight className="h-5 w-5" />
        </button>

        {headerActions}
      </div>

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto bg-wa-chatBg py-3"
      >
        {loading && (
          <p className="py-8 text-center text-sm text-gray-500">
            Loading messages…
          </p>
        )}

        {!loading && loadingHistory && (
          <p className="py-3 text-center text-xs text-gray-500">
            Loading earlier messages…
          </p>
        )}

        {!loading && !hasMoreHistory && messages.length > 0 && (
          <p className="py-3 text-center text-[11px] text-gray-400">
            Beginning of conversation
          </p>
        )}

        {!loading && messages.length === 0 && (
          <p className="py-8 text-center text-sm text-gray-500">
            No messages in this conversation yet.
          </p>
        )}

        {messages.map((message, index) => {
          const previous = messages[index - 1];
          const showDayDivider =
            !previous || dayKey(previous.createdAt) !== dayKey(message.createdAt);

          return (
            <React.Fragment key={message.id}>
              {showDayDivider && (
                <div className="my-3 flex justify-center">
                  <span className="rounded-full bg-white px-3 py-1 text-[11px] font-medium text-gray-500 shadow-sm">
                    {formatDayLabel(message.createdAt)}
                  </span>
                </div>
              )}
              <MessageBubble message={message} onRetry={onRetry} />
            </React.Fragment>
          );
        })}
      </div>

      {children}
    </div>
  );
};

export default ChatCanvas;
